import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Col, Row, Form, Spinner } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { addMake, fetchMakeById, updateMake } from "slices/Masters/make/thunk";
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";

const MakeForm: React.FC = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const isEdit = Boolean(id);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [initialData, setInitialData] = useState({
    Make: "",
  });

  // Fetch data on edit
  useEffect(() => {
    if (isEdit && id) {
      setIsLoading(true);
      dispatch(fetchMakeById(id))
        .unwrap()
        .then((data: any) => {
          setInitialData({
            Make: data.make_name || "",
          });
        })
        .catch(() => toast.error("Failed to fetch Make details"))
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      Make: initialData.Make || "",
    },

    validationSchema: Yup.object({
      Make: Yup.string().trim().required("Please enter Make Name"),
      // .matches(/^[A-Za-z\s]+$/, "Make Name must contain only letters and spaces"),
    }),
    onSubmit: async (values) => {
      const payload = {
        make_id: id,
        make_name: values.Make.trim(),
      };
      setIsSubmitting(true);
      try {
        if (isEdit) {
          await dispatch(updateMake({ ...payload })).unwrap();
          toast.success("Make updated successfully");
        } else {
          await dispatch(addMake(payload)).unwrap();
          clearTableState(TableKeys.MASTER.MAKE);
          toast.success("Make added successfully");
        }
        navigate("/master/make");
      } catch (error: any) {
        toast.error(error?.error || error?.message || "Something went wrong");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  const handleCancelButton = () => {
    navigate("/master/make");
  };

  return (
    <PageContainer>
      <PageBreadcrumb
        title={id ? "Edit Make" : "Add Make"}
        titleLink="/master/make"
        breadcrumbs={[
          { label: "Make", to: "/master/make" },
          { label: id ? "Edit Make" : "Add Make", to: "" },
        ]}
        backNavigationPath={-1}
      />
      <hr />
      {isLoading && isEdit ? (
        <LoadingSpinner />
      ) : (
        <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row className="mb-3">
            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">
                Make <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="Make"
                placeholder="Enter Make"
                value={formik.values.Make}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors.Make && formik.touched.Make}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.Make}
              </Form.Control.Feedback>
            </Col>
          </Row>

          {/* Buttons */}
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
            <CancelButton
              onClick={handleCancelButton}
              disabled={isSubmitting}
            />
            <PrimarySubmitButton
              label={id ? "Update" : "Submit"}
              isLoading={isSubmitting}
              loadingLabel={id ? "Updating..." : "Submitting..."}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default MakeForm;
